/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import { getMe, hasGold } from 'app/gamma/src/selectors/members';
import { State } from 'app/gamma/src/modules/types';
import React, { useCallback, FunctionComponent, Suspense } from 'react';
import { connect } from 'react-redux';
import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import styles from './header.less';
import classNames from 'classnames';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos';
import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
import { defaultRouter } from 'app/common/router';
import { useLazyComponent } from '@trello/use-lazy-component';
const format = forTemplate('header_member_menu');
const viewTitle = forNamespace('view title');

interface Props {
  hasMemberData: boolean;
  member: MemberModel | undefined;
}

enum Screen {
  MemberMenu,
  HelpMenu,
  LanguagePickerMenu,
  ChangePasswordMenu,
}

const HeaderMemberMenuButton: FunctionComponent<Props> = ({
  hasMemberData,
  member,
}) => {
  const { name = '', username = '', id = '', avatarSource = undefined } =
    member || {};
  const userTitle = `${name}${username && ` (${username})`}`;

  const { toggle, push, hide, triggerRef, popoverProps } = usePopover<
    HTMLButtonElement
  >({
    initialScreen: Screen.MemberMenu,
  });
  const onClick = useCallback(() => {
    if (!hasMemberData) {
      return;
    }

    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.MEMBER_MENU,
      method: 'by using member menu button in header',
    });
    toggle();
  }, [hasMemberData, toggle]);

  const HeaderMemberMenu = useLazyComponent(() =>
    import(
      /* webpackChunkName: "header-member-menu-popover" */ 'app/gamma/src/components/popovers/header-member-menu'
    ),
  );

  const Help = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-help" */ 'app/gamma/src/components/popovers/help'
      ),
    {
      preload: popoverProps.isVisible,
    },
  );

  const LanguagePicker = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-language-picker" */ 'app/src/components/LanguagePicker'
      ),
    {
      preload: popoverProps.isVisible,
    },
  );

  const ChangePassword = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "header-member-menu-change-password" */ 'app/gamma/src/components/popovers/change-password'
      ),
    {
      preload: popoverProps.isVisible,
    },
  );

  return (
    <>
      <HeaderButton
        // this className is needed by Desktop for the My Cards MenuItem
        className={classNames(
          'js-open-header-member-menu',
          styles.memberButton,
        )}
        onClick={onClick}
        ref={triggerRef}
        testId={HeaderTestIds.MemberMenuButton}
        title={userTitle}
      >
        <MemberAvatar avatarSource={avatarSource} idMember={id} size={32} />
      </HeaderButton>
      <Popover
        {...popoverProps}
        title={`${member?.name} (${member?.username})`}
      >
        <Suspense fallback={null}>
          <PopoverScreen
            id={Screen.MemberMenu}
            testId={HeaderTestIds.MemberMenuPopover}
            noHorizontalPadding
          >
            <HeaderMemberMenu
              onClickHelp={() => push(Screen.HelpMenu)}
              onClickLanguagePicker={() => push(Screen.LanguagePickerMenu)}
              onClickChangePassword={() => push(Screen.ChangePasswordMenu)}
              onClickShortcuts={() => {
                defaultRouter.setRoute('/shortcuts');
                hide();
              }}
              onClickMenuLink={hide}
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.HelpMenu}
            title={format('help')}
            size="xlarge"
            noHorizontalPadding
          >
            <Help />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.LanguagePickerMenu}
            title={viewTitle('change locale')}
            noHorizontalPadding
          >
            <LanguagePicker memberId={member?.id ?? ''} />
          </PopoverScreen>
          <PopoverScreen id={Screen.ChangePasswordMenu} noHorizontalPadding>
            <ChangePassword />
          </PopoverScreen>
        </Suspense>
      </Popover>
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    hasMemberData: !!getMe(state),
    member: getMe(state),
    hasCrown: hasGold(state),
  };
};

export default connect(mapStateToProps)(HeaderMemberMenuButton);
